import { duration } from 'moment';
import { DeepMap } from 'react-hook-form';
import { SelectOptionElement } from '@odin-labs/components';
import {
  JobsiteUpdateInput,
  SessionDisplayColumns,
} from 'apollo/generated/client-operations';
import { stringifyEmptyFields } from 'utils';
import { HourglassClockIcon } from 'components/icons';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import {
  toggleBorderClasses,
  getAsDurationString,
} from 'containers/jobsiteConfiguration/helpers/utils';
import { Jobsite } from './types';

export const getSiteAccessRulesSectionInputs = ({
  sessionDisplayColumnOptions,
}: {
  sessionDisplayColumnOptions: SelectOptionElement[];
}): TypedFormInputs<EditJobsiteConfigurationFormData['siteAccessRules']> => ({
  onboardingGracePeriodInterval: {
    element: FormInputTypes.Field,
    label: 'Onboarding Grace Period Interval',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: { fieldType: 'number', innerRightLabel: 'Hours' },
  },
  siteAccessInactivityInterval: {
    element: FormInputTypes.Field,
    label: 'Site Access Inactivity Interval',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: { fieldType: 'number', innerRightLabel: 'Days' },
  },
  maximumSessionLengthInterval: {
    element: FormInputTypes.Field,
    label: 'Maximum Session Interval',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: { fieldType: 'number', innerRightLabel: 'Hours' },
  },
  sessionDisplayColumn: {
    element: FormInputTypes.Select,
    label: 'Session Display Value',
    elementProps: {
      options: sessionDisplayColumnOptions,
      icon: HourglassClockIcon,
    },
    validation: { required: true },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
  },
  allowOvernightSessions: {
    element: FormInputTypes.Toggle,
    label: 'Allow sessions spanning overnight',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
});

export const getSiteAccessRulesSectionDefaultValues = (
  jobsite: Jobsite,
  sessionDisplayColumnOptions: SelectOptionElement[],
): EditJobsiteConfigurationFormData['siteAccessRules'] => {
  const {
    onboardingGracePeriod,
    swipeInactivityThreshold,
    maximumSessionLength,
    allowOvernightSessions,
    sessionDisplayColumn,
  } = jobsite ?? {};

  const onboardingGracePeriodInterval =
    onboardingGracePeriod &&
    Math.trunc(duration(onboardingGracePeriod).asHours()).toString();
  const siteAccessInactivityInterval =
    swipeInactivityThreshold &&
    Math.trunc(duration(swipeInactivityThreshold).asDays()).toString();
  const maximumSessionLengthInterval =
    maximumSessionLength &&
    Math.trunc(duration(maximumSessionLength).asHours()).toString();

  return {
    ...stringifyEmptyFields({
      onboardingGracePeriodInterval,
      siteAccessInactivityInterval,
      allowOvernightSessions,
      maximumSessionLengthInterval,
    }),
    sessionDisplayColumn:
      sessionDisplayColumnOptions.find(
        (option) => option.value === sessionDisplayColumn,
      ) ?? null,
  };
};

type SiteAccessRulesSectionUpdateInput = Required<
  Pick<
    JobsiteUpdateInput,
    | 'onboardingGracePeriod'
    | 'swipeInactivityThreshold'
    | 'maximumSessionLength'
    | 'allowOvernightSessions'
    | 'sessionDisplayColumn'
  >
>;

export const getSiteAccessRulesSectionUpdateInput = (
  siteAccessRules: EditJobsiteConfigurationFormData['siteAccessRules'],
  dirtyFields: DeepMap<
    EditJobsiteConfigurationFormData['siteAccessRules'],
    true
  >,
): SiteAccessRulesSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    siteAccessRules,
    dirtyFields,
  );
  return {
    onboardingGracePeriod: getAsDurationString(
      getUpdateInputValue('onboardingGracePeriodInterval'),
      'hours',
    ),
    swipeInactivityThreshold: getAsDurationString(
      getUpdateInputValue('siteAccessInactivityInterval'),
      'days',
    ),
    maximumSessionLength: getAsDurationString(
      getUpdateInputValue('maximumSessionLengthInterval'),
      'hours',
    ),
    allowOvernightSessions: getUpdateInputValue('allowOvernightSessions'),
    sessionDisplayColumn: getUpdateInputValue(
      'sessionDisplayColumn',
    ) as SessionDisplayColumns,
  };
};
